import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Megaphone, Send, Image as ImageIcon, Trash2, Eye, Clock, CheckCircle2, ShieldCheck, RefreshCw, Copy, Plus, Save } from "lucide-react";
import { api, apiErr, inputCls, labelCls } from "./adminShared";

const CAMPAIGN_STATUS = {
  draft: "bg-gray-100 text-gray-700", pending_approval: "bg-amber-100 text-amber-800",
  approved: "bg-blue-100 text-blue-800", scheduled: "bg-purple-100 text-purple-800",
  queued: "bg-amber-100 text-amber-800", sending: "bg-blue-100 text-blue-800",
  sent: "bg-green-100 text-green-800", failed: "bg-red-100 text-red-700",
};
const STATUS_LABEL = {
  connected: "Connected", not_configured: "Not configured", verified: "Verified",
  not_verified: "Not verified", configured: "Configured", sent: "Successful", not_sent: "Not sent",
  enabled: "Enabled", disabled: "Disabled", error: "Error",
};
const good = (v) => ["connected", "verified", "sent", "enabled"].includes(v);
const Pill = ({ v }) => <span className={`text-xs px-2.5 py-1 rounded-full ${good(v) ? "bg-green-100 text-green-800" : v === "error" ? "bg-red-100 text-red-700" : "bg-[#E6DEC3] text-[#635B52]"}`}>{STATUS_LABEL[v] || v}</span>;

const emptyCampaign = { subject: "", body: "", photos: [], button_url: "", button_label: "Book your cleaning", audience: "subscribed" };

function SetupPanel() {
  const [cfg, setCfg] = useState(null);
  const [busy, setBusy] = useState("");
  const load = () => api.get("/admin/marketing/config").then((r) => setCfg(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const run = async (key, fn, msg) => { setBusy(key); try { const r = await fn(); toast.success(msg || "Done"); load(); return r; } catch (e) { toast.error(apiErr(e)); } finally { setBusy(""); } };
  if (!cfg) return null;
  const steps = [
    "Create a Resend account.",
    `Add and verify the sending subdomain ${cfg.subdomain}.`,
    "Add the exact Resend DNS records in Cloudflare (SPF, DKIM, DMARC).",
    "Create a restricted marketing-only API key in Resend.",
    "Add the key via Deployment Secrets (RESEND_MARKETING_API_KEY) — never paste it here.",
    "Configure the signed webhook in Resend to the URL below and add RESEND_MARKETING_WEBHOOK_SECRET.",
    "Send an owner-only test email, then enable sending (MARKETING_SEND_ENABLED=true) only after everything verifies.",
  ];
  return (
    <section className="bg-white rounded-2xl border border-[#E6DEC3] p-5" data-testid="marketing-setup">
      <h3 className="font-serif text-xl text-[#2C2621] flex items-center gap-2"><ShieldCheck size={18} className="text-[#B87333]" /> Marketing Email setup</h3>
      <p className="text-sm text-[#635B52] mt-1">Marketing uses your own Resend account on <strong>{cfg.subdomain}</strong>, kept fully separate from transactional emails (bookings, receipts, reminders). Secrets live in Deployment Secrets and are never shown here.</p>

      <div className="mt-4 grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {[["Account connection", cfg.account], ["Domain", cfg.domain], ["Webhook", cfg.webhook], ["Test email", cfg.test_email], ["Marketing sending", cfg.sending]].map(([label, v]) => (
          <div key={label} className="flex items-center justify-between bg-[#FBF9F5] rounded-xl px-3 py-2.5 border border-[#E6DEC3]">
            <span className="text-sm text-[#2C2621]">{label}</span><Pill v={v} />
          </div>
        ))}
      </div>

      <div className="mt-4 grid sm:grid-cols-2 gap-2 text-sm">
        <div className="bg-[#F5F0E8] rounded-xl p-3"><p className={labelCls}>From</p><p className="text-[#2C2621]">{cfg.from_name} &lt;{cfg.from_email}&gt;</p></div>
        <div className="bg-[#F5F0E8] rounded-xl p-3"><p className={labelCls}>Reply-To</p><p className="text-[#2C2621]">{cfg.reply_to}</p></div>
        <div className="bg-[#F5F0E8] rounded-xl p-3 sm:col-span-2 flex items-center justify-between gap-2">
          <div className="min-w-0"><p className={labelCls}>Webhook URL (add in Resend)</p><p className="text-[#2C2621] truncate">{cfg.webhook_url}</p></div>
          <button onClick={() => { navigator.clipboard.writeText(cfg.webhook_url); toast.success("Copied"); }} className="shrink-0 inline-flex items-center gap-1 border border-[#E6DEC3] rounded-full px-3 py-1.5 text-xs" data-testid="copy-webhook"><Copy size={12} /> Copy</button>
        </div>
      </div>

      <ol className="mt-4 space-y-1.5 text-sm text-[#635B52] list-decimal list-inside" data-testid="setup-checklist">
        {steps.map((s, i) => <li key={i}>{s}</li>)}
      </ol>

      <div className="mt-4 flex flex-wrap gap-2">
        <button onClick={() => run("domain", () => api.post("/admin/marketing/verify-domain"), "Checked domain")} disabled={busy === "domain"} className="inline-flex items-center gap-1 border border-[#E6DEC3] rounded-full px-4 py-2 text-sm disabled:opacity-60" data-testid="verify-domain"><RefreshCw size={14} /> Verify domain</button>
        <button onClick={() => run("test", () => api.post("/admin/marketing/test-owner"), "Owner test sent")} disabled={busy === "test"} className="inline-flex items-center gap-1 border border-[#E6DEC3] rounded-full px-4 py-2 text-sm disabled:opacity-60" data-testid="owner-test"><Send size={14} /> Send owner test</button>
        <button onClick={() => run("sync", () => api.post("/admin/marketing/sync-contacts"), "Contacts synced")} disabled={busy === "sync"} className="inline-flex items-center gap-1 border border-[#E6DEC3] rounded-full px-4 py-2 text-sm disabled:opacity-60" data-testid="sync-contacts"><RefreshCw size={14} /> Sync consented contacts</button>
      </div>
      {!cfg.send_enabled_flag && <p className="mt-3 text-xs text-[#B87333]">Live sending is OFF (MARKETING_SEND_ENABLED=false). Campaigns and birthday rewards are created and queued, but not sent. Owner test emails are allowed once configured.</p>}
    </section>
  );
}

function ContactsPanel() {
  const [contacts, setContacts] = useState([]);
  useEffect(() => { api.get("/admin/marketing/contacts").then((r) => setContacts(r.data)).catch(() => {}); }, []);
  return (
    <section className="bg-white rounded-2xl border border-[#E6DEC3] p-5" data-testid="marketing-contacts">
      <h3 className="font-serif text-xl text-[#2C2621]">Contacts <span className="text-sm text-[#635B52] font-sans">({contacts.length} consented)</span></h3>
      <p className="text-sm text-[#635B52] mt-1">Only people who opted in to marketing appear here. Non-consenting inquiries stay in Leads and never receive campaigns.</p>
      <div className="mt-3 max-h-64 overflow-y-auto divide-y divide-[#E6DEC3]">
        {contacts.map((c, i) => (
          <div key={i} className="py-2 flex items-center justify-between text-sm" data-testid={`contact-${i}`}>
            <span className="text-[#2C2621]">{c.name || "—"} · {c.email}</span>
            <span className="text-xs text-[#635B52]">{c.source}{c.consent_at ? ` · ${new Date(c.consent_at).toLocaleDateString()}` : ""}</span>
          </div>
        ))}
        {!contacts.length && <p className="py-4 text-sm text-[#635B52]">No consented contacts yet.</p>}
      </div>
    </section>
  );
}

export default function AdminMarketing() {
  const [data, setData] = useState({ campaigns: [], can_send: false });
  const [aud, setAud] = useState({ counts: {}, can_send: false });
  const [form, setForm] = useState(emptyCampaign);
  const [editing, setEditing] = useState(null);
  const [preview, setPreview] = useState(false);
  const [busy, setBusy] = useState(false);
  const [schedAt, setSchedAt] = useState("");

  const load = () => {
    api.get("/admin/campaigns").then((r) => setData(r.data)).catch((e) => toast.error(apiErr(e)));
    api.get("/admin/marketing/audiences").then((r) => setAud(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);
  const canSend = data.can_send;

  const reset = () => { setForm(emptyCampaign); setEditing(null); setSchedAt(""); };
  const startEdit = (c) => { setEditing(c.campaign_id); setForm({ subject: c.subject, body: c.body, photos: c.photos || [], button_url: c.button_url || "", button_label: c.button_label || "Book your cleaning", audience: c.audience }); window.scrollTo({ top: 0, behavior: "smooth" }); };

  const save = async () => {
    setBusy(true);
    try {
      if (editing) { await api.put(`/admin/campaigns/${editing}`, form); toast.success("Campaign saved"); }
      else { const r = await api.post("/admin/campaigns", form); setEditing(r.data.campaign_id); toast.success("Draft created"); }
      load();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const uploadPhoto = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try { const r = await api.post("/admin/campaigns/upload-photo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, photos: [...f.photos, r.data.url] })); toast.success("Photo added"); }
    catch (err) { toast.error(apiErr(err)); }
    e.target.value = "";
  };

  const act = async (id, fn, msg) => { try { const r = await fn(); toast.success((r && r.data && r.data.message) || msg); load(); } catch (e) { toast.error(apiErr(e)); } };
  const ensureSaved = async () => { if (!editing) { await save(); } };

  const send = async (c) => {
    if (!window.confirm("Send this campaign now to the selected audience?")) return;
    try { const r = await api.post(`/admin/campaigns/${c.campaign_id}/send`); toast.success(r.data.message || (r.data.sent ? "Sending…" : "Queued")); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const schedule = async (c) => {
    if (!schedAt) { toast.error("Pick a date & time"); return; }
    try { await api.post(`/admin/campaigns/${c.campaign_id}/schedule`, { scheduled_at: new Date(schedAt).toISOString() }); toast.success("Scheduled"); setSchedAt(""); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div className="space-y-6" data-testid="admin-marketing">
      {canSend && <SetupPanel />}
      {canSend && <ContactsPanel />}

      <section className="bg-white rounded-2xl border border-[#E6DEC3] p-5">
        <h3 className="font-serif text-xl text-[#2C2621] flex items-center gap-2"><Megaphone size={18} className="text-[#B87333]" /> {editing ? "Edit campaign" : "New email campaign"}</h3>
        {!canSend && <p className="text-sm text-[#635B52] mt-1">You can create drafts and upload photos, then submit them for the owner to approve and send. Customer email addresses are never shown to staff.</p>}
        <div className="mt-3 space-y-3">
          <div><label className={labelCls}>Subject</label><input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} className={inputCls} data-testid="camp-subject" /></div>
          <div><label className={labelCls}>Message</label><textarea rows={5} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} className={inputCls} placeholder="Write your promotion…" data-testid="camp-body" /></div>
          <div className="grid sm:grid-cols-3 gap-3">
            <div><label className={labelCls}>Audience</label>
              <select value={form.audience} onChange={(e) => setForm({ ...form, audience: e.target.value })} className={inputCls} data-testid="camp-audience">
                <option value="subscribed">Subscribed clients ({aud.counts.subscribed ?? "—"})</option>
                <option value="all_clients">All clients ({aud.counts.all_clients ?? "—"})</option>
                <option value="past_customers">Past customers ({aud.counts.past_customers ?? "—"})</option>
              </select></div>
            <div><label className={labelCls}>Button label</label><input value={form.button_label} onChange={(e) => setForm({ ...form, button_label: e.target.value })} className={inputCls} data-testid="camp-btn-label" /></div>
            <div><label className={labelCls}>Button link</label><input value={form.button_url} onChange={(e) => setForm({ ...form, button_url: e.target.value })} className={inputCls} placeholder="https://…/book" data-testid="camp-btn-url" /></div>
          </div>
          <div>
            <label className={labelCls}>Photos</label>
            <div className="mt-1 flex flex-wrap gap-2 items-center">
              {form.photos.map((p, i) => (
                <div key={i} className="relative">
                  <img src={p} alt="" className="w-20 h-20 object-cover rounded-lg border border-[#E6DEC3]" />
                  <button onClick={() => setForm({ ...form, photos: form.photos.filter((_, j) => j !== i) })} className="absolute -top-2 -right-2 bg-red-600 text-white rounded-full w-5 h-5 text-xs" data-testid={`camp-photo-remove-${i}`}>×</button>
                </div>
              ))}
              <label className="w-20 h-20 rounded-lg border-2 border-dashed border-[#E6DEC3] flex items-center justify-center cursor-pointer text-[#B87333]" data-testid="camp-photo-upload">
                <ImageIcon size={20} /><input type="file" accept="image/*" onChange={uploadPhoto} className="hidden" />
              </label>
            </div>
          </div>
          <div className="flex flex-wrap gap-2 pt-1">
            <button onClick={save} disabled={busy} className="inline-flex items-center gap-1 bg-[#2C2621] text-white rounded-full px-5 py-2.5 text-sm font-medium disabled:opacity-60" data-testid="camp-save">{editing ? <><Save size={15} /> Save</> : <><Plus size={15} /> Create draft</>}</button>
            <button onClick={() => setPreview(!preview)} className="inline-flex items-center gap-1 border border-[#E6DEC3] rounded-full px-4 py-2.5 text-sm" data-testid="camp-preview"><Eye size={15} /> {preview ? "Hide preview" : "Preview"}</button>
            {editing && <button onClick={reset} className="rounded-full px-4 py-2.5 text-sm border border-[#E6DEC3] text-[#635B52]" data-testid="camp-new">New</button>}
          </div>
        </div>

        {preview && (
          <div className="mt-4 border border-[#E6DEC3] rounded-xl p-5 bg-[#FBF9F5]" data-testid="camp-preview-panel">
            <p className="text-xs text-[#B87333] font-semibold uppercase tracking-wider">Preview</p>
            <h4 className="font-serif text-xl text-[#2C2621] mt-1">{form.subject || "(subject)"}</h4>
            {form.body.split("\n").map((p, i) => p.trim() && <p key={i} className="text-sm text-[#2C2621] mt-2">{p}</p>)}
            {form.photos.map((p, i) => <img key={i} src={p} alt="" className="mt-3 rounded-lg max-w-full" />)}
            {form.button_url && <p className="mt-3"><span className="inline-block bg-[#B87333] text-white rounded-full px-5 py-2 text-sm">{form.button_label}</span></p>}
            <p className="mt-4 text-xs text-[#888] border-t border-[#E6DEC3] pt-2">Masaya Hospitality Cleaning Services, LLC · Austin, TX · Unsubscribe link included automatically.</p>
          </div>
        )}
      </section>

      <section>
        <p className={labelCls}>Campaigns</p>
        <div className="mt-2 space-y-2" data-testid="campaign-list">
          {data.campaigns.map((c) => {
            const s = c.stats || {};
            const editable = !["sent", "sending"].includes(c.status);
            return (
              <div key={c.campaign_id} className="bg-white border border-[#E6DEC3] rounded-xl p-4 text-sm">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div>
                    <span className="font-semibold text-[#2C2621]">{c.subject || "(no subject)"}</span>
                    <span className={`ml-2 text-xs px-2 py-0.5 rounded-full ${CAMPAIGN_STATUS[c.status] || "bg-gray-100"}`}>{c.status?.replace(/_/g, " ")}</span>
                    <span className="ml-2 text-xs text-[#635B52]">{c.audience?.replace(/_/g, " ")}</span>
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {editable && <button onClick={() => startEdit(c)} className="border border-[#E6DEC3] rounded-full px-3 py-1.5 text-xs" data-testid={`camp-edit-${c.campaign_id}`}>Edit</button>}
                    {!canSend && editable && <button onClick={() => act(c.campaign_id, () => api.post(`/admin/campaigns/${c.campaign_id}/submit`), "Submitted for approval")} className="border border-[#B87333] text-[#B87333] rounded-full px-3 py-1.5 text-xs" data-testid={`camp-submit-${c.campaign_id}`}>Submit for approval</button>}
                    {canSend && c.status === "pending_approval" && <button onClick={() => act(c.campaign_id, () => api.post(`/admin/campaigns/${c.campaign_id}/approve`), "Approved")} className="border border-blue-300 text-blue-700 rounded-full px-3 py-1.5 text-xs" data-testid={`camp-approve-${c.campaign_id}`}><CheckCircle2 size={12} className="inline" /> Approve</button>}
                    {canSend && editable && <button onClick={() => act(c.campaign_id, () => api.post(`/admin/campaigns/${c.campaign_id}/test`), "Owner test sent")} className="border border-[#E6DEC3] rounded-full px-3 py-1.5 text-xs" data-testid={`camp-test-${c.campaign_id}`}>Test</button>}
                    {canSend && editable && <button onClick={() => send(c)} className="bg-[#B87333] text-white rounded-full px-3 py-1.5 text-xs" data-testid={`camp-send-${c.campaign_id}`}><Send size={12} className="inline" /> Send now</button>}
                    {(canSend || editable) && <button onClick={() => { if (window.confirm("Delete this campaign?")) act(c.campaign_id, () => api.delete(`/admin/campaigns/${c.campaign_id}`), "Deleted"); }} className="border border-red-300 text-red-700 rounded-full px-3 py-1.5 text-xs" data-testid={`camp-delete-${c.campaign_id}`}><Trash2 size={12} /></button>}
                  </div>
                </div>
                {canSend && editable && (
                  <div className="mt-2 flex items-center gap-2">
                    <Clock size={13} className="text-[#635B52]" />
                    <input type="datetime-local" value={schedAt} onChange={(e) => setSchedAt(e.target.value)} className="rounded-lg border border-[#E6DEC3] px-2 py-1 text-xs" data-testid={`camp-sched-input-${c.campaign_id}`} />
                    <button onClick={() => schedule(c)} className="border border-purple-300 text-purple-700 rounded-full px-3 py-1 text-xs" data-testid={`camp-schedule-${c.campaign_id}`}>Schedule</button>
                    {c.scheduled_at && <span className="text-xs text-purple-700">scheduled {new Date(c.scheduled_at).toLocaleString()}</span>}
                  </div>
                )}
                {(c.status === "sent" || c.status === "queued" || (s.recipients || 0) > 0) && (
                  <div className="mt-2 flex flex-wrap gap-3 text-xs text-[#635B52]" data-testid={`camp-stats-${c.campaign_id}`}>
                    <span>Recipients {s.recipients || 0}</span><span>Delivered {s.delivered || 0}</span><span>Opened {s.opened || 0}</span>
                    <span>Clicked {s.clicked || 0}</span><span>Bounced {s.bounced || 0}</span><span>Unsubscribed {s.unsubscribed || 0}</span>
                  </div>
                )}
              </div>
            );
          })}
          {!data.campaigns.length && <p className="text-sm text-[#635B52]">No campaigns yet.</p>}
        </div>
      </section>
    </div>
  );
}
